/**
 * 기준안(웹 문서) 양식 — public.evaluation_web_document_templates (사용자별, RLS: 본인 행만)
 * 테이블이 아직 없으면(마이그레이션 미적용) 예전처럼 localStorage 로 동작합니다.
 * 처음 불러올 때 DB에 해당 슬롯 양식이 없고 localStorage 에 있으면 한 번 올립니다(localStorage 는 백업으로 유지).
 */
import { supabase } from '../supabaseClient';
import type { EvaluationPlanAttachmentSlot } from '../types/evaluationPlan';
import type { EvaluationPlanWebDocumentTemplateStructure } from '../types/evaluationPlanWebDocument';
import {
  deleteWebDocumentTemplate as deleteLocal,
  listWebDocumentTemplates as listLocal,
  renameWebDocumentTemplate as renameLocal,
  upsertWebDocumentTemplate as upsertLocal,
  WEB_DOCUMENT_TEMPLATE_MAX_COUNT,
  type EvaluationPlanWebDocumentTemplate,
} from '../utils/evaluationPlanWebDocumentTemplateStorage';

const TABLE = 'evaluation_web_document_templates';
const MIGRATED_PREFIX = 'cp.evaluationPlan.webDocumentTemplates.migrated.v1:';

type Row = { id: string; slot: string; name: string; template: unknown; created_at: string; updated_at: string };

/** 테이블 없음을 한 번 확인하면 이 세션 동안은 바로 localStorage 사용(오류 반복 방지) */
let tableMissing = false;

function isMissingTable(error: { code?: string; message?: string } | null): boolean {
  if (!error) return false;
  return error.code === 'PGRST205' || error.code === '42P01' || new RegExp(TABLE).test(error.message ?? '');
}

function toTemplate(row: Row): EvaluationPlanWebDocumentTemplate {
  return {
    id: row.id,
    name: row.name,
    savedAt: row.updated_at || row.created_at,
    structure: (row.template ?? {}) as EvaluationPlanWebDocumentTemplateStructure,
  };
}

const sortByName = (list: EvaluationPlanWebDocumentTemplate[]) =>
  [...list].sort((a, b) => a.name.localeCompare(b.name, 'ko'));

async function currentUserId(): Promise<string | null> {
  const { data } = await supabase.auth.getSession();
  return data.session?.user?.id ?? null;
}

async function selectRows(slot: EvaluationPlanAttachmentSlot): Promise<Row[] | null> {
  const { data, error } = await supabase
    .from(TABLE)
    .select('id, slot, name, template, created_at, updated_at')
    .eq('slot', slot);
  if (error) {
    if (isMissingTable(error)) {
      tableMissing = true;
      return null;
    }
    throw new Error(error.message);
  }
  return (data ?? []) as Row[];
}

/** localStorage 양식을 DB로 한 번 옮김(DB 슬롯이 비었을 때만) */
async function migrateLocalOnce(slot: EvaluationPlanAttachmentSlot, userId: string): Promise<boolean> {
  const flag = `${MIGRATED_PREFIX}${userId}:${slot}`;
  try {
    if (localStorage.getItem(flag)) return false;
  } catch {
    return false;
  }
  const local = listLocal(slot).slice(0, WEB_DOCUMENT_TEMPLATE_MAX_COUNT);
  if (local.length) {
    const { error } = await supabase
      .from(TABLE)
      .insert(local.map((t) => ({ slot, name: t.name.trim().slice(0, 60), template: t.structure })));
    if (error) return false;
  }
  try {
    localStorage.setItem(flag, new Date().toISOString());
  } catch {
    /* 무시 */
  }
  return local.length > 0;
}

export async function listEvaluationWebDocumentTemplates(
  slot: EvaluationPlanAttachmentSlot
): Promise<EvaluationPlanWebDocumentTemplate[]> {
  if (tableMissing) return listLocal(slot);
  const userId = await currentUserId();
  if (!userId) return listLocal(slot);
  try {
    let rows = await selectRows(slot);
    if (rows === null) return listLocal(slot);
    if (!rows.length && (await migrateLocalOnce(slot, userId))) {
      rows = (await selectRows(slot)) ?? [];
    } else if (rows.length) {
      // DB에 이미 있으면 다시 옮기지 않도록 표시
      try {
        localStorage.setItem(`${MIGRATED_PREFIX}${userId}:${slot}`, localStorage.getItem(`${MIGRATED_PREFIX}${userId}:${slot}`) || new Date().toISOString());
      } catch {
        /* 무시 */
      }
    }
    return sortByName(rows.map(toTemplate));
  } catch {
    return listLocal(slot);
  }
}

function friendly(error: { code?: string; message?: string }): Error {
  if (/TEMPLATE_LIMIT/.test(error.message ?? '')) return new Error(`양식은 최대 ${WEB_DOCUMENT_TEMPLATE_MAX_COUNT}개까지 저장할 수 있습니다.`);
  if (error.code === '23505') return new Error('이미 같은 이름의 양식이 있습니다.');
  return new Error('양식을 저장하지 못했습니다.');
}

/** 같은 이름이면 덮어쓰기, 아니면 새로 저장(슬롯당 최대 5개) */
export async function upsertEvaluationWebDocumentTemplate(
  slot: EvaluationPlanAttachmentSlot,
  name: string,
  structure: EvaluationPlanWebDocumentTemplateStructure
): Promise<EvaluationPlanWebDocumentTemplate> {
  const trimmed = name.trim();
  if (!trimmed) throw new Error('양식 이름을 입력해 주세요.');
  if (tableMissing || !(await currentUserId())) return upsertLocal(slot, trimmed, structure);
  const rows = await selectRows(slot);
  if (rows === null) return upsertLocal(slot, trimmed, structure);
  const existing = rows.find((r) => r.name === trimmed);
  if (existing) {
    const { data, error } = await supabase
      .from(TABLE)
      .update({ template: structure })
      .eq('id', existing.id)
      .select('id, slot, name, template, created_at, updated_at')
      .single();
    if (error) throw friendly(error);
    return toTemplate(data as Row);
  }
  if (rows.length >= WEB_DOCUMENT_TEMPLATE_MAX_COUNT) {
    throw new Error(`양식은 최대 ${WEB_DOCUMENT_TEMPLATE_MAX_COUNT}개까지 저장할 수 있습니다.`);
  }
  const { data, error } = await supabase
    .from(TABLE)
    .insert({ slot, name: trimmed, template: structure })
    .select('id, slot, name, template, created_at, updated_at')
    .single();
  if (error) throw friendly(error);
  return toTemplate(data as Row);
}

export async function renameEvaluationWebDocumentTemplate(
  slot: EvaluationPlanAttachmentSlot,
  templateId: string,
  name: string
): Promise<void> {
  const trimmed = name.trim();
  if (!trimmed) throw new Error('양식 이름을 입력해 주세요.');
  if (tableMissing || !(await currentUserId())) {
    renameLocal(slot, templateId, trimmed);
    return;
  }
  const { error } = await supabase.from(TABLE).update({ name: trimmed }).eq('id', templateId).eq('slot', slot);
  if (error) {
    if (isMissingTable(error)) {
      tableMissing = true;
      renameLocal(slot, templateId, trimmed);
      return;
    }
    if (error.code === '23505') throw new Error('이미 같은 이름의 양식이 있습니다.');
    throw new Error('양식 이름을 수정하지 못했습니다.');
  }
}

export async function deleteEvaluationWebDocumentTemplate(
  slot: EvaluationPlanAttachmentSlot,
  templateId: string
): Promise<void> {
  if (tableMissing || !(await currentUserId())) {
    deleteLocal(slot, templateId);
    return;
  }
  const { error } = await supabase.from(TABLE).delete().eq('id', templateId).eq('slot', slot);
  if (error) {
    if (isMissingTable(error)) {
      tableMissing = true;
      deleteLocal(slot, templateId);
      return;
    }
    throw new Error('양식을 삭제하지 못했습니다.');
  }
}
